import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { Category } from '../types';
import CategoryManager from './CategoryManager';

interface CategorySelectProps {
  value: string;
  onChange: (value: string, isCustom?: boolean) => void;
  required?: boolean;
  style?: React.CSSProperties;
  disabled?: boolean;
}

const CategorySelect: React.FC<CategorySelectProps> = ({ value, onChange, required, style, disabled }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showManager, setShowManager] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get('/categories');
      setCategories(response.data.data.categories);
    } catch (error) {
      console.error('Fehler beim Laden der Kategorien:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryAdded = () => {
    fetchCategories();
  };

  const getCategoryIcon = (categoryName: string) => {
    const icons: Record<string, string> = {
      INCOME: '💰',
      EXPENSE: '💸',
      FOOD: '🍔',
      TRANSPORT: '🚗',
      HOUSING: '🏠',
      ENTERTAINMENT: '🎮',
      UTILITIES: '💡',
      HEALTHCARE: '⚕️',
      DEBT_PAYMENT: '💳',
      SAVINGS: '🐷',
      INVESTMENT: '📈',
      TRANSFER: '🔄',
      CLOTHING: '👕',
      EDUCATION: '📚',
      ELECTRONICS: '💻',
      OTHER: '📦'
    };
    return icons[categoryName] || '🏷️';
  };

  const selectedCategory = categories.find(cat => cat.name === value);

  if (loading) {
    return (
      <div style={{ 
        padding: '10px 12px', 
        border: '1px solid #e2e8f0', 
        borderRadius: '8px',
        color: '#94a3b8',
        ...style 
      }}>
        Lade Kategorien...
      </div>
    );
  }

  return (
    <>
      <div ref={dropdownRef} style={{ position: 'relative', ...style }}>
        {/* Selected Value Display */}
        <div
          onClick={() => !disabled && setIsOpen(!isOpen)}
          style={{
            padding: '10px 12px',
            border: '2px solid #e2e8f0',
            borderRadius: '8px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: disabled ? '#f1f5f9' : 'white',
            transition: 'all 0.2s',
            borderColor: isOpen ? '#3b82f6' : '#e2e8f0'
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {selectedCategory ? (
              <>
                <span style={{ fontSize: '18px' }}>{getCategoryIcon(selectedCategory.name)}</span>
                <span>{selectedCategory.displayName}</span>
              </>
            ) : (
              <span style={{ color: '#94a3b8' }}>Kategorie wählen...</span>
            )}
          </span>
          <span style={{ 
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
            color: '#64748b'
          }}>
            ▼
          </span>
        </div>

        {/* Dropdown Menu */}
        {isOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '4px',
            backgroundColor: 'white',
            border: '2px solid #e2e8f0',
            borderRadius: '8px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            zIndex: 1000,
            maxHeight: '300px',
            overflowY: 'auto'
          }}>
            {/* Standard Kategorien */}
            <div style={{ padding: '8px 0' }}>
              <div style={{ 
                padding: '8px 12px', 
                fontSize: '12px', 
                fontWeight: 600,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                📋 Standard-Kategorien
              </div>
              {categories.filter(cat => !cat.isCustom).map((category) => (
                <div
                  key={category.name}
                  onClick={() => {
                    onChange(category.name, false);
                    setIsOpen(false);
                  }}
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    backgroundColor: value === category.name ? '#eff6ff' : 'transparent',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    if (value !== category.name) {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (value !== category.name) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <span style={{ fontSize: '18px' }}>{getCategoryIcon(category.name)}</span>
                  <span style={{ 
                    fontWeight: value === category.name ? 600 : 400,
                    color: value === category.name ? '#3b82f6' : '#1e293b'
                  }}>
                    {category.displayName}
                  </span>
                  {value === category.name && (
                    <span style={{ marginLeft: 'auto', color: '#3b82f6' }}>✓</span>
                  )}
                </div>
              ))}
            </div>

            {/* Custom Kategorien */}
            {categories.filter(cat => cat.isCustom).length > 0 && (
              <div style={{ 
                padding: '8px 0',
                borderTop: '1px solid #e2e8f0'
              }}>
                <div style={{ 
                  padding: '8px 12px', 
                  fontSize: '12px', 
                  fontWeight: 600,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  ✨ Deine Kategorien
                </div>
                {categories.filter(cat => cat.isCustom).map((category) => (
                  <div
                    key={category.name}
                    onClick={() => {
                      onChange(category.name, true);
                      setIsOpen(false);
                    }}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      backgroundColor: value === category.name ? '#f0fdf4' : 'transparent',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseEnter={(e) => {
                      if (value !== category.name) {
                        e.currentTarget.style.backgroundColor = '#f8fafc';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (value !== category.name) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <span style={{ fontSize: '18px' }}>{getCategoryIcon(category.name)}</span>
                    <span style={{ 
                      fontWeight: value === category.name ? 600 : 400,
                      color: value === category.name ? '#10b981' : '#1e293b'
                    }}>
                      {category.displayName}
                    </span>
                    {value === category.name && (
                      <span style={{ marginLeft: 'auto', color: '#10b981' }}>✓</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Neue Kategorie Button */}
            <div style={{ 
              borderTop: '1px solid #e2e8f0',
              padding: '8px'
            }}>
              <button
                type="button"
                onClick={() => {
                  setShowManager(true);
                  setIsOpen(false);
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#f8fafc',
                  border: '2px dashed #cbd5e1',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: '#64748b',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f1f5f9';
                  e.currentTarget.style.borderColor = '#94a3b8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8fafc';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                }}
              >
                + Neue Kategorie erstellen
              </button>
            </div>
          </div>
        )}
      </div>

      <CategoryManager 
        isOpen={showManager}
        onClose={() => setShowManager(false)}
        onCategoryAdded={handleCategoryAdded}
      />
    </>
  );
};

export default CategorySelect;